import { Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import type { PersonnelDetail, PersonnelVoluntaryWork } from '../personnelTypes';

/**
 * Voluntary work or involvement in an organization — CS Form 212, section VI.
 *
 * Reads down a column like the work-experience history above it: where they served, for how
 * long, how many hours, and in what capacity. **Most recent first**, matching that panel
 * exactly — two histories of one person running in opposite directions is a reader's mistake
 * waiting to happen.
 *
 * **Searched, sorted and paged in memory**, which is correct here: the rule against
 * client-side filtering is about a PAGED list whose filter applies only to the rows on screen.
 * These arrive whole with the record, so the filter sees everything there is.
 */
export function VoluntaryWorkPanel({
  record,
  onAdd,
  onEdit,
  onDelete,
  onBulkDelete,
  onRestore,
}: {
  record: PersonnelDetail;
  onAdd: () => void;
  onEdit: (row: PersonnelVoluntaryWork) => void;
  onDelete: (row: PersonnelVoluntaryWork) => void;
  onBulkDelete: (rows: PersonnelVoluntaryWork[]) => void;
  onRestore: (row: PersonnelVoluntaryWork) => void;
}) {

  const [showDeleted, setShowDeleted] = useState(false);

  const rows = useMemo(
    () => (showDeleted ? record.voluntaryWorks : record.voluntaryWorks.filter((x) => x.isActive)),
    [record.voluntaryWorks, showDeleted],
  );

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? rows.filter((x) =>
            [x.organizationName, x.organizationAddress, x.positionOrNature]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : rows;

      const field = query.sort?.replace(/^-/, '') ?? 'periodFrom';
      const descending = query.sort?.startsWith('-') ?? false;
      const direction = descending ? -1 : 1;

      const sorted = [...matched].sort((a, b) => {
        if (field === 'numberOfHours') {
          /*
            A NUMBER, so it compares as one — string order would put 9 above 40. Rows with no
            recorded hours sort last whichever way the column is pointed: null here means
            nobody counted them, and burying those among the totals would read as zero hours
            served, which is a different answer the column can also hold.
          */
          const av = a.numberOfHours ?? null;
          const bv = b.numberOfHours ?? null;
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return (av - bv) * direction;
        }

        if (field === 'periodTo') {
          /*
            A row still going has no end date, and it is the MOST recent rather than the least:
            nothing can have ended later than an involvement not yet finished. So it sorts to
            the top descending — the opposite of the missing-hours rule above, because here the
            null is a real answer ("present") rather than an unrecorded one.
          */
          const av = a.periodTo ?? null;
          const bv = b.periodTo ?? null;
          if (av === null && bv === null) return 0;
          if (av === null) return direction;
          if (bv === null) return -direction;
          return av.localeCompare(bv) * direction;
        }

        const pick = (row: PersonnelVoluntaryWork) =>
          field === 'organizationAddress'
            ? row.organizationAddress
            : field === 'positionOrNature'
              ? row.positionOrNature
              : field === 'periodFrom'
                ? row.periodFrom
                : row.organizationName;

        return String(pick(a)).localeCompare(String(pick(b))) * direction;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [rows],
  );

  const state = useDataTable<PersonnelVoluntaryWork>({
    fetchPage,
    pageSize: 10,

    // Most recent first, matching the order the server sends them in and the work-experience
    // panel beside it.
    defaultSort: '-periodFrom',
  });

  const columns: DataColumn<PersonnelVoluntaryWork>[] = [
    {
      /*
        The ANCHOR is the organization, which is what identifies the row — not the position,
        which titles half a career's volunteering "Volunteer" and tells a reader nothing about
        which entry they are looking at.
      */
      key: 'organizationName',
      header: 'Organization',
      width: 240,
      sortable: true,
      render: (x) => <TextCell value={x.organizationName} />,
    },
    {
      key: 'organizationAddress',
      header: 'Address',
      width: 240,
      sortable: true,
      summary: true,
      render: (x) => <TextCell value={x.organizationAddress} />,
    },
    {
      // Calendar dates — the day an involvement started, not an instant.
      key: 'periodFrom',
      header: 'From',
      width: 130,
      sortable: true,
      summary: true,
      render: (x) => <TextCell value={formatDateOnly(x.periodFrom)} mono />,
    },
    {
      key: 'periodTo',
      header: 'To',
      width: 130,
      sortable: true,
      summary: true,
      /*
        "Present", not an em dash — the same treatment the work-experience panel gives the same
        fact. An absent value is a dash everywhere in this product and this is not one: a row
        with no end date is the form's own PRESENT, a real answer saying the involvement is
        still going. A dash would read as "we did not record when it ended".
      */
      render: (x) => <TextCell value={x.periodTo ? formatDateOnly(x.periodTo) : 'Present'} mono />,
    },
    {
      key: 'numberOfHours',
      header: 'Hours',
      width: 110,
      align: 'right',
      sortable: true,
      /*
        `numeric` as well as `mono`: hours are a quantity, so a zero dims like every other zero
        in the product — and a zero here is a real answer (enrolled, never served) rather than
        an absence, which is exactly the distinction the muted treatment draws. An unrecorded
        total is null and renders as an em dash.
      */
      render: (x) => (
        <TextCell
          value={
            x.numberOfHours === null || x.numberOfHours === undefined
              ? null
              : String(x.numberOfHours)
          }
          mono
          numeric
        />
      ),
    },
    {
      key: 'positionOrNature',
      header: 'Position / nature of work',
      width: 220,
      sortable: true,
      render: (x) => <TextCell value={x.positionOrNature} />,
    },
    {
      /*
        A panel whose rows can be deleted owes a status column: without one a deleted row
        carries no marker while Show deleted is on, which is the one state a row must never be
        silent about. `<DataTablePanel>` moves it to the end of the data columns itself.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        x.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <DataTablePanel<PersonnelVoluntaryWork>
        id="personnel-voluntary-work"
        state={state}
        /*
          The first column opens the row as a key/value preview. A table row compares records;
          it is a poor way to read ONE, because the columns are spread across a scroll and the
          narrow ones truncate. The preview is built from these same `columns`, so it cannot
          drift from the table or miss one somebody adds.
        */
        // The size of the modal that EDITS this record, so reading a row and correcting it
        // happen at one width rather than the dialog changing shape between them.
        rowPreview={{ title: 'Voluntary work', size: '2xl' }}
        columns={columns}
        rowKey={(x) => x.id}
        searchPlaceholder="Search organization, address or position"
        onCreate={onAdd}
        createPermission="personnel.update"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before editing the record.'
        }
        /*
          The kebab's entries. The shell owns the gutter, the menu and the right-click
          shortcut — this is only what goes IN the menu, and the same array feeds the table
          and the list, so the two cannot offer different actions on one row.

          Delete and Restore are the two halves of one state, so only the applicable one is
          offered. That is not the disable-for-state case: "restore a live row" is not
          something the reader is part-way through, it is simply not an action here.
        */
        rowActions={(x) => [
          {
            key: `edit-${x.id}`,
            label: 'Edit',
            permission: 'personnel.update',
            disabledReason: x.isActive
              ? null
              : 'This record is deleted. Restore it before editing.',
            onClick: () => onEdit(x),
          },
          x.isActive
            ? {
                key: `delete-${x.id}`,
                label: 'Delete',
                permission: 'personnel.update',
                destructive: true,
                onClick: () => onDelete(x),
              }
            : {
                key: `restore-${x.id}`,
                label: 'Restore',
                permission: 'personnel.update',
                onClick: () => onRestore(x),
              },
        ]}
        otherInfo={(x) => ({
          createdAt: x.createdAt,
          createdByName: x.createdByName,
          updatedAt: x.updatedAt,
          updatedByName: x.updatedByName,
        })}
        /*
          The checkbox gutter is ON, which is the default — `selectable={false}` is only for a
          table nothing can ever be done to, never for one whose bulk action is merely unbuilt.
          So the bulk action ships with it.
        */
        bulkActions={(selected) => {
          const deletable = selected.filter((x) => x.isActive);

          return [
            {
              key: 'delete-voluntary-work',
              label: `Delete ${deletable.length} record${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? 'This employee is deleted. Restore them before editing the record.'
                : deletable.length === 0
                  ? 'Every selected record is already deleted.'
                  : null,
              onClick: () => onBulkDelete(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No voluntary work recorded yet."
        emptySearchMessage="No voluntary work matches that search."
      />
    </ProfileSection>
  );
}
